/**
 * Generates a wa.me URL for WhatsApp click-to-chat.
 *
 * @param {string} phone       - The customer's raw phone number
 * @param {object} details     - Account and customer details
 * @param {string} details.customerName   - Name of the customer
 * @param {string} details.accountId      - The shortened account ID
 * @param {number} details.balance        - Outstanding balance
 * @param {number} [details.unpaidDuration] - Number of unpaid days/weeks/months (for reminder)
 * @param {string} [details.durationUnit]   - 'days', 'weeks', or 'months'
 * @param {string} [details.consoleUrl]     - Full customer console URL to share
 * @param {string} [details.mode]           - 'reminder' (default) | 'statement'
 * @param {string} [details.financierName]  - The financier's display name
 */

const CUSTOMER_LOGIN_URL = 'https://finaculator.web.app/customer-login';

export const generateWhatsAppLink = (phone, details) => {
  // 1. Format the phone number
  let cleanPhone = String(phone).replace(/\D/g, '');
  if (cleanPhone.length === 10) {
    cleanPhone = '91' + cleanPhone;
  }

  const balanceStr = `₹${Number(details.balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}`;

  // Always include console link + login prompt when consoleUrl is available
  const consoleLine = details.consoleUrl
    ? `\n\n📊 *View your account statement:*\n${details.consoleUrl}\n\n🔐 *Login to your dashboard:*\n${CUSTOMER_LOGIN_URL}`
    : '';

  let message;

  const financierName = details.financierName || 'Finaculator';

  if (details.mode === 'statement') {
    message = `Hello ${details.customerName},

Here is your account statement for A/C *${details.accountId}* from ${financierName}.

Your current outstanding balance is *${balanceStr}*.${consoleLine}

Thank you.`;
  } else {
    // Default: payment reminder
    message = `Hello ${details.customerName},

This is a gentle reminder from ${financierName} regarding your account (${details.accountId}).

Your account currently has an outstanding balance of *${balanceStr}* and has been unpaid for *${details.unpaidDuration} ${details.durationUnit}*.${consoleLine}

Please make the payment at your earliest convenience to avoid any penalties. Thank you.`;
  }

  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
};


/**
 * Generates a full customer summary report for all their accounts.
 *
 * @param {string} phone        - Customer's phone number
 * @param {object} customer     - Customer data { name, clientId }
 * @param {Array}  accounts     - Array of account objects
 * @param {string} [consoleUrl] - Optional public console URL for the customer
 * @param {string} [financierName] - Optional financier name
 * @returns {string}            - wa.me URL with full report as text
 */
export const generateCustomerReport = (phone, customer, accounts, consoleUrl, financierName) => {
  let cleanPhone = String(phone).replace(/\D/g, '');
  if (cleanPhone.length === 10) cleanPhone = '91' + cleanPhone;

  const today = new Date();

  const getElapsed = (date, type) => {
    const d = new Date(date);
    if (type === 'weekly') return Math.round((today - d) / (1000 * 3600 * 24 * 7));
    if (type === 'monthly') {
      return (today.getFullYear() - d.getFullYear()) * 12 + today.getMonth() - d.getMonth();
    }
    return Math.round((today - d) / (1000 * 3600 * 24)) - 1;
  };

  const typeLabel = (t) => t === 'weekly' ? 'weeks' : t === 'monthly' ? 'months' : 'days';

  let totalDisbursed = 0;
  let totalRecovered = 0;
  let totalOutstanding = 0;

  const lines = accounts.map((acc, i) => {
    const paidDayCount = acc.entries
      ? Object.values(acc.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.dayCount)), 0) : 0;
    const paidAmount = acc.entries
      ? Object.values(acc.entries).reduce((s, e) => s + (e.isUpfrontInterest ? 0 : Number(e.amountPaid)), 0) : 0;
    const elapsed = getElapsed(acc.date, acc.installmentType);
    const remaining = acc.installmentPeriod - paidDayCount;
    const isOverdue = elapsed > acc.installmentPeriod && remaining > 0;
    const overduePeriods = isOverdue ? elapsed - acc.installmentPeriod : 0;
    const arrears = Math.min(remaining, Math.max(0, elapsed - paidDayCount));
    const unit = typeLabel(acc.installmentType);

    totalDisbursed += Number(acc.amount);
    totalRecovered += paidAmount;
    totalOutstanding += (Number(acc.amount) - paidAmount);

    let status;
    if (paidDayCount >= acc.installmentPeriod) {
      status = '✅ Completed';
    } else if (isOverdue) {
      status = `🔴 OVERDUE by ${overduePeriods} ${unit}`;
    } else if (arrears > 0) {
      status = `⚠️ ${arrears} ${unit} in arrears`;
    } else {
      status = `✅ On track (${remaining} ${unit} left)`;
    }

    return `*${i + 1}. A/C ${acc.accountId.split('_')[0]}* (${(acc.installmentType || 'daily').toUpperCase()})
   Principal: ₹${Number(acc.amount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
   Installment: ₹${Number(acc.installmentAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })} / ${unit.slice(0, -1)}
   Paid: ${paidDayCount}/${acc.installmentPeriod} ${unit} — ₹${paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
   Outstanding: ₹${(Number(acc.amount) - paidAmount).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
   Status: ${status}`;
  });

  const dateStr = today.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const consoleLine = consoleUrl
    ? `\n\n📊 *View your full statement online:*\n${consoleUrl}\n\n🔐 *Login to your dashboard:*\n${CUSTOMER_LOGIN_URL}`
    : `\n\n🔐 *Login to your dashboard:*\n${CUSTOMER_LOGIN_URL}`;

  const message = `📋 *CUSTOMER REPORT* — ${dateStr}
━━━━━━━━━━━━━━━━━━

*Customer:* ${customer.name}
*ID:* ${customer.clientId.split('_')[0]}
*Active Accounts:* ${accounts.length}

*ACCOUNT DETAILS:*
${lines.join('\n\n')}

━━━━━━━━━━━━━━━━━━
*SUMMARY:*
💰 Total Disbursed: ₹${totalDisbursed.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
✅ Total Recovered: ₹${totalRecovered.toLocaleString('en-IN', { maximumFractionDigits: 3 })}
🔴 Outstanding: ₹${totalOutstanding.toLocaleString('en-IN', { maximumFractionDigits: 3 })}${consoleLine}

_Powered by ${financierName || 'Finaculator'}_`;

  return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
};

